import {
  FacetStore,
  FacetResponses,
  FacetConfig,
  LookupSourceIdentifier,
  LookupData,
  FacetRootStore,
  Facet,
  MediaSourceId,
  MediaSourceData,
} from "@globalgiving/vast-frontend-common"
import { action, comparer, computed, observable } from "mobx"
import { reportInvalidFacet } from "~/helpers"
import { InvalidFacetInfo } from "~/types"

export class StartVettingStore implements FacetRootStore {
  @observable accessor _facets: Map<string, Facet> = new Map()
  @observable accessor lookupSources: Map<LookupSourceIdentifier, LookupData | null> = new Map()
  @observable accessor media: Map<MediaSourceId, MediaSourceData | null> = new Map()
  @observable accessor invalidFacet: InvalidFacetInfo | null = null

  @computed
  get facets(): FacetStore[] {
    return Array.from(this._facets.values())
  }

  @computed
  get facetsMap(): Map<string, Facet> {
    return this._facets
  }

  constructor(facetConfigs: FacetConfig[]) {
    this.buildFacets(facetConfigs)
  }

  @action
  updateValues(responses: FacetResponses): void {
    Object.entries(responses).forEach(([key, values]) => {
      const targetFacet = this._facets.get(key)
      if (targetFacet) {
        targetFacet.updateValues(values)
      }
    })
  }

  @action
  update(facetConfigs: FacetConfig[]): void {
    const configNames = facetConfigs.map((config) => config.name)
    const toRemove = Array.from(this._facets.keys()).filter((key) => !configNames.includes(key))
    toRemove.forEach((name) => {
      this._facets.delete(name)
    })

    this.buildFacets(facetConfigs)
  }

  @action
  private buildFacets(facetConfigs: FacetConfig[]): void {
    this.invalidFacet = null

    for (const config of facetConfigs) {
      try {
        const existingFacet = this._facets.get(config.name)
        if (existingFacet) {
          existingFacet.update(config)
        } else {
          this._facets.set(config.name, new Facet(config, this))
        }
      } catch (error: unknown) {
        this.invalidFacet = reportInvalidFacet(config, error)
        this._facets.clear()
        return
      }
    }
  }

  @action
  updateLookupSource(lookupSourceIdentifier: LookupSourceIdentifier, data: LookupData | null): void {
    this.lookupSources.set(lookupSourceIdentifier, data)
  }

  @action
  updateMedia(id: MediaSourceId, data: MediaSourceData | null): void {
    /* Skip the write when nothing actually changed. */
    if (comparer.structural(this.media.get(id), data)) {
      return
    }

    this.media.set(id, data)
  }
}
